/**
 * CHOOSING A PLAN, THE SAME WAY IN EVERY MODULE (module reviews, 18 Oct 2026).
 *
 * The village, downsizing, granny flat and cashflow modules saved a NEW record on every click of
 * "Select Strategy" — re-clicking "Selected ✓", or choosing again from the full list, each added one —
 * and never marked any as chosen, so Home and the menu could only ever say "N plans saved, none chosen".
 *
 * `withChosenPlan` is the one rule: choosing a plan replaces any earlier save of the SAME plan (same
 * type, same engine strategy id, or the same name for a hand-built one), un-chooses the rest of that
 * type, and adds the new record as the chosen one. Plans of other types are untouched — choosing a
 * village plan does not un-choose the aged care plan.
 */
export interface PlanRecordLike {
    id?: string | number;
    type?: string;
    name?: string;
    source?: string;
    chosen?: boolean;
    inputs?: Record<string, unknown> | null;
    [key: string]: unknown;
}

const sameEnginePlan = (a: PlanRecordLike, b: PlanRecordLike): boolean => {
    const ida = a.inputs?.engineStrategyId;
    const idb = b.inputs?.engineStrategyId;
    if (ida != null && idb != null) return String(ida) === String(idb) && sameContext(a, b);
    return !!a.name && a.name === b.name && (a.source ?? '') === (b.source ?? '') && sameContext(a, b);
};

/** Two saves of the "same" plan for a DIFFERENT village, home or support cost are different plans. */
const sameContext = (a: PlanRecordLike, b: PlanRecordLike): boolean =>
    JSON.stringify(a.inputs?.planContext ?? null) === JSON.stringify(b.inputs?.planContext ?? null);

export function withChosenPlan<T extends PlanRecordLike>(prev: readonly T[] | null | undefined, record: T): T[] {
    const list = (prev || []).filter((p) => !(p.type === record.type && sameEnginePlan(p, record)));
    return [
        ...list.map((p) => (p.type === record.type && p.chosen ? { ...p, chosen: false } : p)),
        { ...record, chosen: true },
    ];
}

/** The chosen plan of a type, or null. */
export function chosenPlanOf<T extends PlanRecordLike>(plans: readonly T[] | null | undefined, type: string): T | null {
    return (plans || []).find((p) => p.type === type && p.chosen === true) ?? null;
}
